import React, { useEffect, useRef } from "react";
import CategoryGroupComponent from "../CategoryGroupComponent/CategoryGroupComponent";
import { categoryPills } from "../../../service/CategoryPills";
import ProductCardComponent from "../../ProductCardComponent/ProductCardComponent";
import { useTheme } from "../../../hooks/ThemeContext";
import TimeService from "../../../service/TimeService";
import ProductCardComponentSleek from "../../ProductCardComponent/ProductCardComponentSleek";
import { getRemoteEnv } from '../../../config/remoteEnv';
import { bestBlackOrWhite } from "../../../utils/contrastColor";
import Logo from "../../Logo";
import LogoPlatform from "../../LogoPlatform";
import { useConfig } from "../../../hooks/ConfigProvider";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import { useOrderMode, ORDER_MODES } from "../../../hooks/OrderModeContext";
import StorePauseBanner from "../../StorePause/StorePauseBanner";
import "./CatalogueComponentStyles.css"


type Props = {
    addToCart: any,
    productData: any[],
    menuSettings: any[],
    containerRef?: any,
    menuLoading?: boolean,
    setMenuLoading?: any,
    openingHours: any
}

const CatalogueComponent = (props: Props) => {

    const [searchValue, setSearchValue] = React.useState("");
    const [activeCategory, setActiveCategory] = React.useState<number>(0);
    const [activeMenu, setActiveMenu] = React.useState("");
    const [categoryData, setCategoryData] = React.useState<any[]>([]);
    const categoriesRef = React.useRef<(HTMLDivElement | null)[]>([]); // Add typing for categoriesRef
    const categoryContainerRef = React.useRef<HTMLDivElement | null>(null);
    const buttonsRef = React.useRef<any[]>([]);

    const { theme } = useTheme();
    const orderMode = useOrderMode();
    // Footer (in the end-of-menu spacer): the store's config entry.
    const { config } = useConfig();
    const { restaurantId } = useRestaurant();
    const footerRestaurant = config?.restaurants?.[restaurantId];

    const activeButtonRef = useRef<any>(null);

    useEffect(() => {
        if (activeButtonRef.current) {
            activeButtonRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [activeMenu]);

    // Switching menus starts the new menu from the top — carrying the old
    // scroll depth into a different menu strands the viewer mid-list. Both
    // scrollers reset: the window (mobile doc-flow) and the inner container
    // (desktop constrained layout); whichever isn't scrolling is a no-op.
    // Skipped on mount so the initial menu load doesn't yank the page.
    const menuSwitchedRef = useRef(false);
    useEffect(() => {
        if (!menuSwitchedRef.current) {
            if (activeMenu !== "") menuSwitchedRef.current = true;
            return;
        }
        window.scrollTo({ top: 0 });
        if (props.containerRef?.current) props.containerRef.current.scrollTop = 0;
        setActiveCategory(0);
    }, [activeMenu]);


    React.useEffect(() => {

        if (activeMenu !== ""){
            // let uniqueCategoriesArray = Array.from(new Set(props.productData.filter((x) => x.menus.includes(activeMenu)).map(item => item.category)));

            // const itemsToMove = ["Desserts", "Kids"];
            // const filteredArray = uniqueCategoriesArray.filter(item => !itemsToMove.includes(item));
          
            // // Check if "Desserts" and/or "Kids" are in the original array
            // const desserts = uniqueCategoriesArray.includes("Desserts") ? ["Desserts"] : [];
            // const kids = uniqueCategoriesArray.includes("Kids") ? ["Kids"] : [];

            // uniqueCategoriesArray = [...filteredArray, ...desserts, ...kids]

            let uniqueCategoriesArray = props.menuSettings.filter((x:any) => x.menuName === activeMenu)[0].categories;
            console.log("UCA: ", uniqueCategoriesArray)

            const data = uniqueCategoriesArray.map((category: any) => ({
                categoryId: category.id,
                categoryText: category.name,
                disabled: category.enabled == false || false,
                deleted: category.deleted || false
            }));

            setCategoryData(data)
        }

    }, [activeMenu, props.productData])


    React.useEffect(() => {

        const handleMenuSettings = async () => {

            if (props.menuSettings) {
                for (let menuItem of props.menuSettings) {

                    if (menuItem.enabled && !menuItem.deleted){
                        let menuActive = await TimeService.checkActiveMenu(props.menuSettings, menuItem.menuName);

                        if (menuActive) {
                            setActiveMenu(menuItem.menuName)
                            props.setMenuLoading(false);
                            break;
                        }
                    }
                }
                if (!activeMenu && props.menuSettings.length > 0) {

                    for (let menuItem of props.menuSettings){
                        if (menuItem.enabled && !menuItem.deleted) {
                            setActiveMenu(menuItem.menuName);
                            props.setMenuLoading(false);
                            break;
                        }
                    }


                }
            }
        }

        handleMenuSettings();

    }, [props.menuSettings])


    const scrollToCategory = (index: number) => {
        const categoryElement = categoriesRef.current[index];
        const scrollContainer = props.containerRef.current;
        if (categoryElement && scrollContainer) {
          // Get the sticky header element
          const stickyHeaderElement = scrollContainer.parentElement?.querySelector('[style*="position: sticky"]') as HTMLElement;
          const stickyHeaderHeight = stickyHeaderElement ? stickyHeaderElement.offsetHeight : 0;

          // Doc-flow mode (mobile): the container no longer scrolls — the
          // DOCUMENT does (so Safari's toolbar can collapse). Scroll the
          // window instead, allowing for the sticky top bar too.
          const containerScrolls = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
          if (!containerScrolls) {
            const topBar = document.querySelector(".top-bar") as HTMLElement | null;
            const topBarSticky = topBar && getComputedStyle(topBar).position === "sticky" ? topBar.offsetHeight : 0;
            const categoryRect = categoryElement.getBoundingClientRect();
            window.scrollTo({
              top: window.scrollY + categoryRect.top - stickyHeaderHeight - topBarSticky,
              behavior: "smooth",
            });
            return;
          }

          // Calculate the position of the category element relative to the scroll container's content
          const containerRect = scrollContainer.getBoundingClientRect();
          const categoryRect = categoryElement.getBoundingClientRect();

          // Calculate how much we need to scroll, accounting for sticky header
          const scrollAmount = scrollContainer.scrollTop + (categoryRect.top - containerRect.top) - stickyHeaderHeight;

          scrollContainer.scrollTo({
            top: scrollAmount,
            behavior: "smooth",
          });
        }
    };

    React.useEffect(() => {
        const scrollContainer = props.containerRef.current;
    
        // Ensure the container exists before adding event listeners
        if (scrollContainer) {
          const handleScroll = () => {
            // Container mode: measure from the (viewport-pinned) container's
            // top. Doc-flow mode: the container itself moves with the page,
            // so measure from below the sticky bars instead.
            const containerScrolls = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
            let containerTop = scrollContainer.getBoundingClientRect().top;
            if (!containerScrolls) {
              const stickyHeaderElement = scrollContainer.parentElement?.querySelector('[style*="position: sticky"]') as HTMLElement;
              const topBar = document.querySelector(".top-bar") as HTMLElement | null;
              containerTop =
                (stickyHeaderElement ? stickyHeaderElement.offsetHeight : 0) +
                (topBar && getComputedStyle(topBar).position === "sticky" ? topBar.offsetHeight : 0);
            }

            // Find the category closest to the top of the container
            let closestIndex = 0;
            let closestDistance = Infinity;

            categoriesRef.current.forEach((ref, index) => {
              if (ref) {
                const categoryTop = ref.getBoundingClientRect().top - containerTop;
                const distance = Math.abs(categoryTop);
                
                // Find whichever category is closest to the top
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestIndex = index;
                }
              }
            });

            if (activeCategory !== closestIndex) {
              setActiveCategory(closestIndex);
              scrollToStart(closestIndex);
            }
          };
    
          scrollContainer.addEventListener("scroll", handleScroll);
          // Doc-flow mode: the document scrolls instead of the container —
          // the rect-based math above works identically, it just needs the
          // events. Both listeners coexist harmlessly.
          window.addEventListener("scroll", handleScroll, { passive: true });

          // Cleanup event listener
          return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
            window.removeEventListener("scroll", handleScroll);
          };
        }
    }, [props.containerRef, activeCategory]);


    const scrollToStart = (index: number) => {

        if (buttonsRef.current[index]) {

            const scrollContainer = categoryContainerRef.current;

            if (scrollContainer) {

                const categoryPosition = buttonsRef.current[index].getBoundingClientRect().left - scrollContainer.getBoundingClientRect().left;
                scrollContainer.scrollTo({
                    left: categoryPosition + scrollContainer.scrollLeft,
                    behavior: "smooth",
                });
            }
        }
    };

    if (!props.menuSettings) {
        return <></>
    }

    return (
        <div style={{display: "flex", flexDirection: "column", width: "100%", height: "100%", marginBottom: "30px"}}>
            {/* top offset comes from CSS (.catalogue-sticky): 0 in the
                constrained layout, below the sticky top bar in doc-flow. */}
            <div className="catalogue-sticky" style={{position: "sticky", backgroundColor: theme.backgroundColor, zIndex: 1100}}>
              <div className="catalogue-controls-inner">
                {/* Website paused by the venue (switch / closure) — or a
                    heads-up for a closure in the next few days. */}
                <StorePauseBanner style={{ marginTop: "10px" }} />
                {/* Fulfilment indicator — one compact line. Tapping it (when
                    there's a choice) reopens the chooser page. */}
                {/* Always visible while a mode is active: mode on the LEFT
                    (tap reopens the chooser when there's more than one
                    enabled mode — closed modes stay selectable to browse),
                    live schedule indicator on the RIGHT ("Until 2 AM",
                    "Open 24 hours", "Closed · opens 11 AM"). */}
                {/* !ready: the store config is still loading — a mode from
                    the URL is already active though, so show the chip at
                    once instead of popping it in seconds later (the status
                    label follows when the schedule is known). */}
                {(orderMode.orderingEnabled || !orderMode.ready) && orderMode.mode && (() => {
                  const current = ORDER_MODES.find((m) => m.id === orderMode.mode);
                  const canChange = orderMode.enabledModes.length > 1;
                  const status = orderMode.mode ? orderMode.modeInfo[orderMode.mode] : undefined;
                  return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", paddingTop: "10px", marginBottom: "-10px" }}>
                      <button
                        onClick={canChange ? () => orderMode.clearMode() : undefined}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "5px 0px",
                          borderRadius: "999px",
                          border: "none",
                          backgroundColor: "transparent",
                          color: theme.textColor,
                          fontSize: "12.5px",
                          fontWeight: 600,
                          cursor: canChange ? "pointer" : "default",
                          fontFamily: "inherit",
                          WebkitTapHighlightColor: "transparent",
                        }}
                      >
                        <span style={{ opacity: 0.6 }}>Ordering:</span>
                        <span style={{ fontWeight: 700 }}>{current?.label}</span>
                        {canChange && <span style={{ fontSize: "10px", opacity: 0.6 }}>▼</span>}
                      </button>
                      {status && (
                        <span style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: status.open ? theme.textColor : "#e5484d",
                          opacity: status.open ? 0.55 : 0.9,
                        }}>
                          {status.label}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {props.menuSettings.filter((x) => x.enabled && !x.deleted).length > 1 && <div style={{width: "100%", borderRadius: "30px", marginTop: "20px", marginBottom: "0px"}}>
                    { props.menuSettings &&

                        (theme.menuButtons === theme.menuButtons) ? (
                            <div style={{
                                overflowX: "auto",
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                                WebkitOverflowScrolling: "touch",
                            }} className="hide-scrollbar">
                                <div style={{
                                    display: "flex",
                                    gap: "12px",
                                    minWidth: "min-content",
                                }}>
                                    {props.menuSettings
                                        .filter((x) => x.enabled && !x.deleted)
                                        .sort((a, b) => a.order - b.order)
                                        .map((item, index) => (
                                            <button
                                                key={index}
                                                ref={item.menuName === activeMenu ? activeButtonRef : null}
                                                onClick={() => setActiveMenu(item.menuName)}
                                                style={{
                                                    padding: "8px 16px",
                                                    backgroundColor: activeMenu === item.menuName ? theme.categoryIndicatorColor : "transparent",
                                                    color: activeMenu === item.menuName
                                                        ? bestBlackOrWhite(theme.categoryIndicatorColor)
                                                        : bestBlackOrWhite(theme.backgroundColor),
                                                    border: `1px solid ${theme.borderColor}`,
                                                    borderRadius: "20px",
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    whiteSpace: "nowrap",
                                                    flex: "1 0 120px",
                                                    minWidth: "120px"
                                                }}
                                            >
                                                {item.menuName}
                                            </button>
                                        ))
                                    }
                                </div>
                            </div>
                        ) :

                        (
                            <select value={activeMenu} onChange={(e) => setActiveMenu(e.target.value)} style={{width: "95%", height: "40px", lineHeight: "40px", paddingLeft: "20px", color: theme.textColor, border: "none", backgroundColor: "transparent", backgroundImage: "none", outline: "none", boxSizing: "border-box"}} >
                                { 
                                    props.menuSettings.filter((x) => x.enabled && !x.deleted).sort((a, b) => a.order - b.order).map((item, index) => {
                                        return (
                                            <option key={index} value={item.menuName} style={{textAlign: "center"}}>{item.menuName}</option>
                                        )
                                    })
                                }
                            </select>
                        )


                    }
                </div>
                
                }
                <input placeholder="What would you like to eat?" onChange={(e) => {setSearchValue(e.target.value)}} style={{width: "100%", height: "40px", lineHeight: "40px", paddingLeft: "20px", border: `1px solid ${theme.borderColor}`, marginTop: "10px", marginBottom: "10px", backgroundColor: theme.searchBarColor || "#ffffff", color: theme.textColor, borderRadius: "30px", boxSizing: "border-box"}} />
                {categoryData.length>0 && <CategoryGroupComponent categoryContainerRef={categoryContainerRef} activeMenu={activeMenu} buttonsRef={buttonsRef} scrollToStart={scrollToStart} categoryData={categoryPills(categoryData, props.productData)} selectedCategory={categoryData[activeCategory] && categoryData[activeCategory].categoryId} scrollToCategory={scrollToCategory} />
                }
              </div>
            </div>


            <div className="catalogue-body" style={{width: "100%", padding: "0", boxSizing: "border-box"}}>
            <div className="catalogue-inner">

            {categoryData.map((category, index) => {


            if (props.productData.filter((p) => (p.productName.toLowerCase().includes(searchValue.toLowerCase())) && (p.productType !== "additional") && (p.category === category.categoryId) && (!p.isDisabled && !p.isDeleted)).length !== 0){
            
                return (
                    <section className="catalogue-section" key={index}>
                        
                        {/* {!category.deleted && !category.disabled && <div key={index} className={"num-" + index} ref={(el) => (categoriesRef.current[index] = el)} style={{width: "100%", textAlign: "left"}}><p key={index} style={{fontWeight: "500", padding: "10px", marginTop: "10px", marginBottom: "10px", borderBottom: `1px solid ${theme.borderColor}`, borderTop: `1px solid ${theme.borderColor}`, textAlign: "center"}}>{category.categoryText}</p></div>} */}
                        
                        {!category.deleted && !category.disabled && (
                            <div
                                key={index}
                                className={"num-" + index}
                                ref={(el) => (categoriesRef.current[index] = el)}
                                style={{ width: "100%", textAlign: "left" }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        gap: "12px",
                                        padding: "0 4px",
                                        margin: "32px 0 14px 0",
                                    }}
                                >
                                    <h2
                                        style={{
                                            fontFamily: 'var(--app-display-font, "Bricolage Grotesque", sans-serif)',
                                            fontSize: "20px",
                                            fontWeight: 600,
                                            letterSpacing: "-0.015em",
                                            lineHeight: 1.1,
                                            margin: 0,
                                            fontOpticalSizing: "auto",
                                        }}
                                    >
                                        {category.categoryText}
                                    </h2>
                                    <span
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            letterSpacing: "0.12em",
                                            textTransform: "uppercase",
                                            opacity: 0.5,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {props.productData.filter((p) =>
                                            (p.productName.toLowerCase().includes(searchValue.toLowerCase())) &&
                                            (p.productType !== "additional") &&
                                            (p.category === category.categoryId) &&
                                            (!p.isDisabled && !p.isDeleted) &&
                                            (p.menus.includes(activeMenu))
                                        ).length} items
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        <div className="catalogue-grid">
                        {props.productData.filter((p) => (p.productName.toLowerCase().includes(searchValue.toLowerCase())) && (!category.disabled) && (!category.deleted) && (p.productType !== "additional") && (!p.isDisabled && !p.isDeleted) && (p.category === category.categoryId) && (p.menus.includes(activeMenu))).map((product, index) => (
                            getRemoteEnv().fullCatalogueView ?
                             <ProductCardComponentSleek key={product.productId} productId={product.productId} productData={props.productData} productName={product.productName} productDescription={product.productDescription} productPrice={product.productPrice} currencySymbol={getRemoteEnv().currencySymbol || "A"} menus={product.menus} activeMenu={activeMenu} additionalOptions={props.productData.filter((p) => product.additionalOptions && product.additionalOptions.includes(p.productId))} userChoices={product.userChoices || []} addToCart={props.addToCart} productThumbnailUrl={product.productThumbnailUrl} menuSettings={props.menuSettings} openingHours={props.openingHours}/>
                            :
                            <ProductCardComponent key={product.productId} product={product} productId={product.productId} productData={props.productData} productName={product.productName} productDescription={product.productDescription} productPrice={product.productPrice} currencySymbol={getRemoteEnv().currencySymbol || "A"} menus={product.menus} activeMenu={activeMenu} additionalOptions={props.productData.filter((p) => product.additionalOptions && product.additionalOptions.includes(p.productId))} userChoices={product.userChoices || []} addToCart={props.addToCart} productThumbnailUrl={product.productThumbnailUrl} menuSettings={props.menuSettings} openingHours={props.openingHours}/>
                        ))}
                        </div>
                    </section>                        
                )
            }
            else {
                return <></>
            }
            })}

            {/* Spacer to allow last category to scroll to top — doubles as
                the menu footer: brand logo, store details, platform credit. */}
            <div style={{
                width: "100%",
                height: "calc(100vh - 250px)", // Height based on viewport minus approximate header height
                minHeight: "300px",
                maxHeight: "500px",
                background: `
                    linear-gradient(180deg, ${theme.backgroundColor} 0%, ${theme.backgroundColor}ee 15%, ${theme.backgroundColor}cc 30%, ${theme.backgroundColor}99 50%, ${theme.backgroundColor}66 70%, ${theme.backgroundColor}33 85%, ${theme.backgroundColor}00 100%),
                    repeating-linear-gradient(45deg, ${theme.borderColor}44 0px, ${theme.borderColor}44 10px, transparent 10px, transparent 20px),
                    repeating-linear-gradient(-45deg, ${theme.borderColor}33 0px, ${theme.borderColor}33 10px, transparent 10px, transparent 20px)
                `,
                backgroundBlendMode: "overlay",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                boxSizing: "border-box",
                padding: "32px 16px",
                textAlign: "center",
                color: theme.textColor,
            }}>
                <div style={{ height: "52px", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.9 }}>
                    <Logo useCachedLogo fallbackName={footerRestaurant?.name || getRemoteEnv().appName} wordmarkFontSize="clamp(26px, 7vw, 34px)" />
                </div>
                <div style={{ width: "44px", height: "1px", backgroundColor: theme.borderColor, opacity: 0.6, margin: "6px 0" }} />
                <a
                    href="https://www.dineplatform.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", color: "inherit", textDecoration: "none" }}
                >
                    <span style={{ fontSize: "11.5px", fontWeight: 600, letterSpacing: "0.04em", opacity: 0.45 }}>
                        Powered by DinePlatform
                    </span>
                    {/* "Dine" flips black/white against the page background;
                        "Platform" stays royal blue (LogoPlatform's contract). */}
                    <span style={{ display: "inline-block", opacity: 0.85, transform: "scale(0.78)", transformOrigin: "top center" }}>
                        <LogoPlatform light={bestBlackOrWhite(theme.backgroundColor) === "#000000"} />
                    </span>
                </a>
            </div>

            </div>
            </div>







            


        </div>
    )
}

export default CatalogueComponent;